import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Building, Mail, Phone, Globe, MapPin, Clock, DollarSign, PackageMinus, ImageOff, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { AuthImage } from "@/components/kit/AuthImage";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { ConfirmDialog } from "@/components/feedback/ConfirmDialog";
import { CompanyDrawer } from "./CompanyDrawer";
import { NotificationPreferencesCard } from "./NotificationPreferencesCard";
import { companyService } from "@/services/companyService";
import { apiClient, formatApiErrorDetail } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { isOwnerRole } from "@/lib/permissions/permissions";

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-0.5 truncate text-sm text-foreground">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const owner = isOwnerRole(user?.role);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);

  const companyQuery = useQuery({ queryKey: ["company"], queryFn: companyService.get });
  const c = companyQuery.data;

  const removeLogo = async () => {
    try {
      await companyService.removeLogo();
      toast.success("Logo removed");
      qc.invalidateQueries({ queryKey: ["company"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRemoveOpen(false);
    }
  };

  const wipeAllData = async () => {
    setWipeBusy(true);
    try {
      const { data } = await apiClient.post("/admin/wipe-all-data", { confirm: "WIPE" });
      const totals = Object.values(data.deleted || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`All tenant data wiped (${totals} records). Only your owner account remains.`);
      setWipeConfirm("");
      qc.invalidateQueries();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setWipeBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="page-settings">
      <PageHeader
        sticky
        title="Company Settings"
        description="Configure your 3PL company profile, branding and platform preferences."
        actions={
          owner ? (
            <Button className="gap-2" onClick={() => setDrawerOpen(true)} disabled={!c} data-testid="edit-company-button">
              <Pencil className="h-4 w-4" />
              Edit Company
            </Button>
          ) : null
        }
      />

      {companyQuery.isLoading ? (
        <LoadingState label="Loading company…" />
      ) : companyQuery.isError ? (
        <ErrorState onRetry={() => companyQuery.refetch()} />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="rounded-xl border border-border bg-card p-6 shadow-soft">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border border-border bg-muted/40">
              <AuthImage
                path={c.logo_path}
                alt="Company logo"
                className="h-full w-full object-contain"
                fallback={<ImageOff className="h-6 w-6 text-muted-foreground" />}
              />
            </div>
            <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">{c.name}</h2>
            <div className="mt-2">
              <StatusBadge label={c.status === "active" ? "Active" : "Inactive"} tone={c.status === "active" ? "green" : "neutral"} />
            </div>
            {owner && c.logo_path && (
              <Button variant="outline" size="sm" className="mt-4 gap-2 text-error hover:text-error" onClick={() => setRemoveOpen(true)} data-testid="remove-logo-button">
                <Trash2 className="h-4 w-4" /> Remove Logo
              </Button>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              There is one 3PL company per installation. Only the 3PL Owner can edit these details.
            </p>
          </section>

          <section className="lg:col-span-2">
            <div className="grid grid-cols-1 divide-y divide-border rounded-xl border border-border bg-card px-6 shadow-soft sm:grid-cols-2 sm:gap-x-8 sm:divide-y-0">
              <div className="sm:divide-y sm:divide-border">
                <InfoRow icon={Building} label="Company Name" value={c.name} />
                <InfoRow icon={Mail} label="Email" value={c.email} />
                <InfoRow icon={Phone} label="Phone" value={c.phone} />
                <InfoRow icon={Globe} label="Website" value={c.website} />
              </div>
              <div className="sm:divide-y sm:divide-border">
                <InfoRow icon={MapPin} label="Address" value={c.address} />
                <InfoRow icon={Clock} label="Timezone" value={c.timezone} />
                <InfoRow icon={DollarSign} label="Currency" value={c.currency} />
                <InfoRow icon={PackageMinus} label="Low Stock Threshold" value={String(c.low_stock_threshold)} />
              </div>
            </div>
          </section>
        </div>
      )}

      <CompanyDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        company={c}
        onSaved={() => qc.invalidateQueries({ queryKey: ["company"] })}
      />
      <NotificationPreferencesCard />
      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title="Remove company logo?"
        description="The logo will be removed from your branding. You can upload a new one anytime."
        confirmLabel="Remove logo"
        destructive
        onConfirm={removeLogo}
      />

      {owner && (
        <section
          data-testid="danger-zone"
          className="rounded-xl border-2 border-red-200 bg-red-50/50 p-6 shadow-soft"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-red-700">Danger Zone — Wipe All Data</h3>
              <p className="mt-1 text-sm text-red-700/80">
                Permanently deletes every warehouse, user, client, VA agency, store, order,
                inventory record, return, invitation and activity log in this environment.
                Only your Product Owner account and the company profile are preserved. This
                action is <b>irreversible</b>.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input
                  value={wipeConfirm}
                  onChange={(e) => setWipeConfirm(e.target.value)}
                  placeholder='Type "WIPE" to enable the button'
                  className="sm:max-w-xs border-red-300 focus-visible:ring-red-400"
                  data-testid="wipe-confirm-input"
                />
                <Button
                  variant="destructive"
                  disabled={wipeConfirm !== "WIPE" || wipeBusy}
                  onClick={wipeAllData}
                  className="gap-2"
                  data-testid="wipe-all-data-button"
                >
                  <Trash2 className="h-4 w-4" />
                  {wipeBusy ? "Wiping…" : "Wipe All Data"}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
